import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, Plus, Trash2 } from "lucide-react";
import { useId } from "react";

import { Button } from "@/components/ui/button";
import { FileUpload } from "@/components/ui/file-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type DeliveryAssetType = "CODE_ZIP" | "LINK" | "CREDENTIAL" | "FILE" | "NOTE";

export type AssetDraft = {
  id: string;
  type: DeliveryAssetType;
  label: string;
  value: string;
  sensitive: boolean;
};

const TYPE_LABEL: Record<DeliveryAssetType, string> = {
  CODE_ZIP: "Code ZIP",
  LINK: "Link",
  CREDENTIAL: "Credential",
  FILE: "File",
  NOTE: "Note",
};

const TYPE_HELPER: Record<DeliveryAssetType, string> = {
  CODE_ZIP: "Source code bundle (.zip, up to 500 MB).",
  LINK: "Hosted URL — admin dashboard, repo, deployment, etc.",
  CREDENTIAL: "Login info or API key. Mark sensitive to hide by default.",
  FILE: "Any other downloadable: brand assets, design exports, docs.",
  NOTE: "Plain text — context, gotchas, deployment notes.",
};

export function AssetEditor({
  assets,
  onChange,
}: {
  assets: AssetDraft[];
  onChange: (next: AssetDraft[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = assets.findIndex((a) => a.id === active.id);
    const newIndex = assets.findIndex((a) => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(assets, oldIndex, newIndex));
  }

  function addAsset(type: DeliveryAssetType) {
    onChange([
      ...assets,
      {
        id: cryptoRandom(),
        type,
        label: "",
        value: "",
        sensitive: type === "CREDENTIAL",
      },
    ]);
  }

  function updateAsset(id: string, patch: Partial<AssetDraft>) {
    onChange(assets.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }

  function removeAsset(id: string) {
    onChange(assets.filter((a) => a.id !== id));
  }

  return (
    <div className="space-y-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={assets.map((a) => a.id)}
          strategy={verticalListSortingStrategy}
        >
          {assets.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              No additional assets yet. Add a link, credential, file, or note
              below.
            </div>
          ) : (
            <ul className="space-y-2">
              {assets.map((asset) => (
                <SortableRow
                  key={asset.id}
                  asset={asset}
                  onChange={(patch) => updateAsset(asset.id, patch)}
                  onRemove={() => removeAsset(asset.id)}
                />
              ))}
            </ul>
          )}
        </SortableContext>
      </DndContext>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Add:</span>
        {(Object.keys(TYPE_LABEL) as DeliveryAssetType[]).map((t) => (
          <Button
            key={t}
            type="button"
            variant="outline"
            size="xs"
            onClick={() => addAsset(t)}
          >
            <Plus className="size-3" />
            {TYPE_LABEL[t]}
          </Button>
        ))}
      </div>
    </div>
  );
}

function SortableRow({
  asset,
  onChange,
  onRemove,
}: {
  asset: AssetDraft;
  onChange: (patch: Partial<AssetDraft>) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.id });
  const labelId = useId();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-lg border bg-background",
        isDragging && "opacity-50 shadow-lg ring-1 ring-primary/40",
      )}
    >
      <div className="flex items-start gap-2 p-3">
        <button
          type="button"
          className="mt-1 inline-flex cursor-grab items-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>

        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
              {TYPE_LABEL[asset.type]}
            </span>
            <span className="text-xs text-muted-foreground">
              {TYPE_HELPER[asset.type]}
            </span>
          </div>

          <div className={cn(
            "grid gap-2",
            asset.type === "CREDENTIAL" && !asset.label && !asset.value
              ? "grid-cols-1"
              : "sm:grid-cols-[12rem_1fr]"
          )}>
            {asset.type === "CREDENTIAL" && !asset.label && !asset.value ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  { id: "User", label: "Username" },
                  { id: "Pass", label: "Password" },
                  { id: "Key", label: "API Key" },
                ].map((btn) => (
                  <button
                    key={btn.id}
                    type="button"
                    onClick={() => onChange({ label: btn.label })}
                    className="h-10 w-full min-w-0 rounded-xl border border-[#000000] bg-background px-3.5 py-2 text-[14px] font-medium text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] transition-[border-color,box-shadow,background-color] duration-150 hover:bg-muted/30 focus-visible:border-foreground/40 focus-visible:ring-2 focus-visible:ring-ring/40"
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label htmlFor={`${labelId}-label`} className="sr-only">
                    Label
                  </Label>
                  <Input
                    id={`${labelId}-label`}
                    value={asset.label}
                    onChange={(e) => onChange({ label: e.target.value })}
                    placeholder="Label"
                  />
                </div>

                <div className="space-y-1">
                  {asset.type === "NOTE" ? (
                    <Textarea
                      value={asset.value}
                      onChange={(e) => onChange({ value: e.target.value })}
                      placeholder="Notes for the customer (markdown OK)"
                      rows={3}
                    />
                  ) : asset.type === "CODE_ZIP" || asset.type === "FILE" ? (
                    <FileValue asset={asset} onChange={onChange} />
                  ) : (
                    <Input
                      value={asset.value}
                      onChange={(e) => onChange({ value: e.target.value })}
                      placeholder={
                        asset.type === "LINK"
                          ? "https://…"
                          : asset.label || "username / password / api key"
                      }
                      autoFocus={!!asset.label}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 text-xs">
            <button
              type="button"
              onClick={() => onChange({ sensitive: !asset.sensitive })}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 transition-colors",
                asset.sensitive
                  ? "bg-amber-500/15 text-amber-700"
                  : "bg-muted text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={asset.sensitive}
            >
              {asset.sensitive ? (
                <EyeOff className="size-3" />
              ) : (
                <Eye className="size-3" />
              )}
              {asset.sensitive ? "Sensitive (hidden by default)" : "Visible"}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onRemove}
              aria-label="Remove asset"
            >
              <Trash2 className="size-3" />
              Remove
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

function FileValue({
  asset,
  onChange,
}: {
  asset: AssetDraft;
  onChange: (patch: Partial<AssetDraft>) => void;
}) {
  const purpose = asset.type === "CODE_ZIP" ? "delivery_code" : "delivery_asset";
  return (
    <FileUpload
      purpose={purpose}
      value={asset.value}
      onChange={(url) => onChange({ value: url })}
      helper={
        asset.type === "CODE_ZIP"
          ? "ZIP only · up to 500 MB"
          : "Any file · up to 50 MB"
      }
      showPreview={false}
    />
  );
}

function cryptoRandom(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `tmp-${Math.random().toString(36).slice(2, 10)}`;
}
